import type { PropsWithChildren } from 'react';

import { cn } from '@/utils/ui';

import { Dialog, DialogContent, DialogDescription, DialogTrigger } from '..';
import { DialogFooterGroup } from './DialogFooterGroup';
import { DialogHeaderGroup } from './DialogHeaderGroup';

type TDialogComposedProps = PropsWithChildren & {
  readonly Tigger?: React.ReactNode;
  readonly TitleIcon?: React.ReactNode;
  readonly description?: React.ReactNode;
  readonly footerActionCallback?: (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  readonly footerActionDisabled?: boolean;
  readonly footerActionLabel?: string;
  readonly footerActionLoading?: boolean;
  readonly footerCloseCallback?: (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  readonly footerCloseLabel?: string;
  readonly hideCloseButton?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly open?: boolean;
  readonly size?: 'md' | 'lg' | 'xl';
  readonly title?: string;
  readonly className?: string;
  readonly headerChildren?: React.ReactNode;
};

const DialogComposed = ({
  Tigger,
  TitleIcon,
  children,
  description,
  footerActionCallback,
  footerActionDisabled,
  footerActionLabel,
  footerActionLoading,
  footerCloseCallback,
  footerCloseLabel,
  hideCloseButton = false,
  onOpenChange,
  open,
  size = 'md',
  title,
  className,
  headerChildren,
}: TDialogComposedProps) => {
  const showTitle = !!title || !!description;
  const showFooter = !!footerActionCallback || !!footerCloseCallback;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {Tigger && <DialogTrigger asChild>{Tigger}</DialogTrigger>}
      <DialogDescription />
      <DialogContent
        hideCloseButton={hideCloseButton}
        size={size}
        className={cn(showTitle && 'gap-4', className)}
      >
        {showTitle && (
          <DialogHeaderGroup
            title={title}
            description={description}
            TitleIcon={TitleIcon}
          >
            {headerChildren}
          </DialogHeaderGroup>
        )}
        {children}
        {showFooter && (
          <DialogFooterGroup
            footerActionCallback={footerActionCallback}
            footerActionDisabled={footerActionDisabled}
            footerActionLabel={footerActionLabel}
            footerActionLoading={footerActionLoading}
            footerCloseCallback={footerCloseCallback}
            footerCloseLabel={footerCloseLabel}
            showDivider={!!children}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export { DialogComposed };
